import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toggleSaved } from "@/lib/saved";
import { Download, Music2, Video, Heart, Sparkles } from "lucide-react";

export default function Gift() {
  const { code } = useParams();
  const [gift, setGift] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/gifts/${code}`)
      .then((r) => setGift(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "unknown"));
  }, [code]);

  const save = () => {
    if (gift?.song_id || gift?.code) {
      // save via song_id if we had it; else nothing (gift references song via download url)
    }
  };

  if (error === "not_found") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <Link to="/" className="font-display text-3xl inline-block mb-8">Sanctum</Link>
          <h1 className="font-display text-3xl">This gift is missing.</h1>
          <p className="text-neutral-500 mt-3 text-sm">The link may have been mistyped or expired.</p>
          <Link to="/" className="btn-pill btn-primary mt-8 inline-flex text-sm">Back to home</Link>
        </div>
      </div>
    );
  }
  if (!gift) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">Opening your gift…</div>;
  }

  return (
    <div className="min-h-screen bg-[--sanctum-bg] px-6 py-14 flex flex-col items-center">
      <Link to="/" className="font-display text-3xl mb-10">Sanctum</Link>

      <div className="max-w-lg w-full text-center">
        <p className="eyebrow mb-3 flex items-center justify-center gap-1.5">
          <Sparkles size={12} strokeWidth={1.5} className="text-[--sanctum-accent]" />
          A song for you
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-light leading-tight">
          {gift.from} sent you<br /><em className="not-italic">{gift.title}</em>.
        </h1>

        {gift.cover_url && (
          <div className="mt-10 art-frame rounded-2xl overflow-hidden aspect-square max-w-xs mx-auto shadow-[0_20px_60px_rgba(0,0,0,0.08)]" data-testid="gift-cover">
            <img src={gift.cover_url} alt={gift.title} className="w-full h-full object-cover" />
          </div>
        )}

        <p className="mt-6 text-neutral-600 text-sm">
          {gift.artist} · {gift.format?.toUpperCase()}
        </p>

        {gift.gift_message && (
          <blockquote className="mt-6 mx-auto max-w-md text-sm italic text-neutral-600 bg-[#F5F0E6] border-l-2 border-[--sanctum-accent] rounded-r-lg py-3 px-4 text-left leading-relaxed" data-testid="gift-message">
            "{gift.gift_message}"
          </blockquote>
        )}

        {gift.paid && gift.download_url ? (
          <div className="mt-10 flex flex-col items-center gap-3">
            <a
              href={gift.download_url}
              target="_blank" rel="noreferrer" download
              data-testid="gift-download"
              className="btn-pill btn-primary text-sm"
            >
              <Download size={14} strokeWidth={1.5} /> Download {gift.format?.toUpperCase()}
            </a>
            <p className="text-xs text-neutral-400">Keep this link — it's yours to revisit anytime.</p>
          </div>
        ) : (
          <div className="mt-10 text-sm text-neutral-500">
            This gift hasn't finished processing yet. Come back in a minute.
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-[--sanctum-border] text-sm">
          <p className="text-neutral-500">Discover more songs on <Link to="/songs" className="text-neutral-900 hover:text-neutral-600 underline underline-offset-4">Sanctum</Link>.</p>
        </div>
      </div>
    </div>
  );
}
